import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { type BackgroundCheckMissingDataType } from "@src/appV2/OpenShifts/ShiftAction/types";

import { DialogFooter } from "../../components/DialogFooter";
import { BoostedPayLabel } from "../../Pay/BoostedPayLabel";
import { HourlyPayLabel } from "../../Pay/HourlyPayLabel";
import { TotalPayLabel } from "../../Pay/TotalPayLabel";
import { SHIFT_DISCOVERY_SHIFT_BOOKED_MODAL_PATH } from "../../ShiftDiscovery/paths";
import { type NavigateToModalFunction } from "../../ShiftDiscovery/useShiftModalsDataContext";
import { InstantPayIndicator } from "../../WorkerShifts/components/InstantPayIndicator";
import { type ShiftBoost } from "../ShiftBoost/api/schema";
import { type ModalShiftData, type ModalShiftOfferData, type ModalWorkplaceData } from "../types";
import { BookShiftButton } from "./BookShiftButton";
import { RateNegotiationAction } from "./RateNegotiationAction";

interface RateNegotiationFooterProps {
  offer: ModalShiftOfferData;
  shift: ModalShiftData;
  isShiftAlreadyBooked: boolean;
  availableShiftBoost?: ShiftBoost;
  minutesElapsed: number;
  workplace: ModalWorkplaceData;
  navigateToModal: NavigateToModalFunction;
  isTestAccount: boolean;
  shouldHideBookCta: boolean;
  isInstantBookDisabled: boolean;
  isBookable: boolean;
  handleDisabledButtonClick: () => void;
  missingBackgroundCheckDataTypes: BackgroundCheckMissingDataType[];
}

export function RateNegotiationFooter(props: RateNegotiationFooterProps) {
  const {
    offer,
    shift,
    isShiftAlreadyBooked,
    availableShiftBoost,
    minutesElapsed,
    workplace,
    navigateToModal,
    isTestAccount,
    shouldHideBookCta,
    isInstantBookDisabled,
    isBookable,
    handleDisabledButtonClick,
    missingBackgroundCheckDataTypes = [],
  } = props;

  return (
    <DialogFooter withShadow orientation="vertical">
      <Stack direction="column" spacing={3} sx={{ width: "100%", paddingLeft: 5 }}>
        {!isShiftAlreadyBooked && (
          <Stack direction="row" spacing={2} alignItems="center">
            <HourlyPayLabel size="large" hourlyPay={offer.attributes.formattedHourlyPay} />

            <Stack direction="row" sx={{ width: "100%", justifyContent: "flex-end" }}>
              {isDefined(availableShiftBoost) ? (
                <BoostedPayLabel
                  size="medium"
                  totalPayBeforeBoost={offer.attributes.formattedTotalPay}
                  totalWithBoostPay={formatDollarsAsUsd(
                    offer.attributes.totalPay +
                      availableShiftBoost.attributes.amount -
                      (minutesElapsed / 60) * offer.attributes.hourlyPay
                  )}
                />
              ) : (
                <TotalPayLabel
                  size="medium"
                  totalPay={formatDollarsAsUsd(
                    offer.attributes.totalPay - (minutesElapsed / 60) * offer.attributes.hourlyPay
                  )}
                />
              )}
              {shift?.attributes.isInstantPay && <InstantPayIndicator sx={{ marginLeft: 4 }} />}
            </Stack>
          </Stack>
        )}

        <Stack direction="row" spacing={2} sx={{ width: "100%", py: 2 }}>
          <RateNegotiationAction
            useNewRateNegotiationUi
            rateNegotiationStatus={shift.rateNegotiation?.status}
            rateNegotiationLastOfferBy={shift.rateNegotiation?.lastOfferBy}
            rateNegotiationLastOfferPayRate={shift.rateNegotiation?.lastOffer?.hourlyPay}
            rateNegotiationId={shift.rateNegotiation?.id}
            shiftId={shift.id}
            workplaceId={workplace.id}
            navigateToModal={navigateToModal}
            missingBackgroundCheckDataTypes={missingBackgroundCheckDataTypes}
          />

          {!isTestAccount && !shouldHideBookCta && isDefined(shift) && isDefined(offer) && (
            <BookShiftButton
              fullWidth
              isInstantBookDisabled={isInstantBookDisabled}
              disabled={!isBookable}
              shift={shift}
              rateNegotiationId={undefined}
              missingBackgroundCheckDataTypes={missingBackgroundCheckDataTypes}
              onBook={() => {
                navigateToModal(SHIFT_DISCOVERY_SHIFT_BOOKED_MODAL_PATH, {
                  pathParams: { shiftId: shift.id },
                });
              }}
              onDisabledButtonClick={handleDisabledButtonClick}
            />
          )}
        </Stack>
      </Stack>
    </DialogFooter>
  );
}
